import React, { useState } from 'react';
import {
  TextField, Button, Typography, Alert, Stack, Divider, Box
} from '@mui/material';
import AppCard from '../common/AppCard';
import AppDialog from '../common/AppDialog';
import PrivacyTipOutlinedIcon from '@mui/icons-material/PrivacyTipOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import TableViewOutlinedIcon from '@mui/icons-material/TableViewOutlined';
import ShuffleIcon from '@mui/icons-material/Shuffle';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import api from '../../services/api';
import { useData } from '../../contexts/DataContext';
import { exportTransactionsToCSV } from '../../utils/csvExportUtils';

const actionBtnSx = { borderRadius: 3, textTransform: 'none', fontWeight: 600, boxShadow: 'none', px: 2.5 };

export default function DataPrivacyCard({ notify }) {
  const { transactions, categories, budgets, rules, accounts, actions, anonymizeAllData } = useData();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [deleting, setDeleting] = useState(false);

  const handleExportJson = () => {
    const payload = {
      app: 'Budget Clues',
      exportedAt: new Date().toISOString(),
      transactions, categories, budgets, rules, accounts, actions
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `budget-clues-export-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    notify('Full data export downloaded.');
  };

  const handleExportCsv = () => {
    exportTransactionsToCSV(transactions, categories);
    notify('Transactions CSV downloaded.');
  };

  const closeDeleteDialog = () => {
    setDeleteOpen(false);
    setDeleteConfirmText('');
    setDeleteError('');
  };

  const handleDeleteAllData = async () => {
    setDeleteError('');
    setDeleting(true);
    try {
      await api.delete('/settings/data');
      // Simplest reliable way to drop all cached client state after a wipe
      window.location.reload();
    } catch (err) {
      setDeleteError(err.response?.data?.message || 'Failed to delete data.');
      setDeleting(false);
    }
  };

  return (
    <AppCard title="Data & Privacy" icon={<PrivacyTipOutlinedIcon />}>
      {/* Three sections: side by side on desktop, stacked on mobile */}
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2.5}
        divider={<Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' } }} />}
      >
        {/* Export */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <Typography variant="body2" fontWeight={600} gutterBottom>
            Export your data
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, flex: 1 }}>
            Download everything Budget Clues holds for you. It's yours to keep.
          </Typography>
          <Stack direction="row" spacing={1.5} sx={{ flexWrap: 'wrap', gap: 1.5 }}>
            <Button
              variant="outlined"
              startIcon={<FileDownloadOutlinedIcon />}
              onClick={handleExportJson}
              sx={actionBtnSx}
            >
              Everything (JSON)
            </Button>
            <Button
              variant="outlined"
              startIcon={<TableViewOutlinedIcon />}
              onClick={handleExportCsv}
              disabled={!transactions?.length}
              sx={actionBtnSx}
            >
              Transactions (CSV)
            </Button>
          </Stack>
        </Box>

        {/* Demo mode */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <Typography variant="body2" fontWeight={600} gutterBottom>
            Demo mode
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, flex: 1 }}>
            Instantly randomize all transaction and budget numbers and descriptions for privacy.
            Handy for screenshots. Only affects this session; your real data is unchanged.
          </Typography>
          <Button
            variant="outlined"
            startIcon={<ShuffleIcon />}
            onClick={() => { anonymizeAllData(); notify('All transactions and budgets anonymized for demo mode.'); }}
            sx={actionBtnSx}
          >
            Anonymize this session
          </Button>
        </Box>

        {/* Delete all data */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <Typography variant="body2" fontWeight={600} color="error.main" gutterBottom>
            Start fresh
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, flex: 1 }}>
            Permanently delete all transactions, categories, budgets, rules, accounts and actions.
            Your login, preferences and bank connection are kept.
          </Typography>
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteSweepIcon />}
            onClick={() => setDeleteOpen(true)}
            sx={actionBtnSx}
          >
            Delete all data
          </Button>
        </Box>
      </Stack>

      <AppDialog
        open={deleteOpen}
        onClose={closeDeleteDialog}
        title="Delete all your data?"
        primaryLabel={deleting ? 'Deleting…' : 'Delete everything'}
        primaryColor="error"
        onPrimary={handleDeleteAllData}
        primaryDisabled={deleting || deleteConfirmText.trim().toUpperCase() !== 'DELETE'}
      >
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Alert severity="error">
            This permanently deletes every transaction, category, budget, rule, account and
            action. It cannot be undone. Consider exporting your data first.
          </Alert>
          {deleteError && <Alert severity="error">{deleteError}</Alert>}
          <TextField
            label='Type DELETE to confirm'
            value={deleteConfirmText}
            onChange={e => setDeleteConfirmText(e.target.value)}
            fullWidth
            size="small"
            autoComplete="off"
          />
        </Stack>
      </AppDialog>
    </AppCard>
  );
}
